/**
 * =============================================================================
 * useSwipeToClose
 * Versão: 1.0.0
 *
 * Hook reutilizável que adiciona o gesto "arrastar para baixo para fechar" em
 * containers de modal/sheet no mobile. Inspirado em apps nativos (Instagram,
 * iOS Sheets) — permite o usuário descartar o modal sem tocar no botão X.
 *
 * Comportamento:
 *   - Só ativa quando `enabled === true` (caller decide via `useIsMobile()`).
 *   - O gesto só começa se o toque iniciar com o conteúdo rolável interno
 *     já no topo (scrollTop === 0). Isso evita conflito com scroll vertical
 *     interno (ex.: lista de comentários, timeline) — primeiro o usuário rola
 *     até o topo, depois um novo "pull" descarta o modal.
 *   - Ignora gestos majoritariamente horizontais (carrosséis, abas).
 *   - Aplica resistência (factor 0.9) e fade do overlay.
 *   - Threshold: deltaY > 120px OU velocidade > 0.6 px/ms → fecha.
 *
 * Uso:
 *   const { contentRef, contentStyle, overlayStyle, handlers } =
 *     useSwipeToClose({ enabled: isMobile, onClose: () => onOpenChange(false) });
 *
 *   <div ref={contentRef} style={contentStyle} {...handlers}> ... </div>
 * =============================================================================
 */
import { useCallback, useRef, useState, type CSSProperties } from 'react';

interface UseSwipeToCloseOptions {
  /** Quando false, o hook é no-op (retorna handlers vazios). */
  enabled: boolean;
  /** Callback para fechar o modal/sheet (geralmente onOpenChange(false)). */
  onClose: () => void;
  /** Distância mínima (px) para considerar fechamento. Default: 120. */
  threshold?: number;
  /** Velocidade mínima (px/ms) para fechar mesmo com pouco deslocamento. Default: 0.6. */
  velocityThreshold?: number;
}

interface UseSwipeToCloseResult {
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  contentStyle: CSSProperties;
  overlayStyle: CSSProperties;
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onTouchCancel: (e: React.TouchEvent) => void;
  };
  /** Indica se o conteúdo está sendo arrastado (útil para suprimir transições). */
  isDragging: boolean;
}

export function useSwipeToClose({
  enabled,
  onClose,
  threshold = 120,
  velocityThreshold = 0.6,
}: UseSwipeToCloseOptions): UseSwipeToCloseResult {
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Estado do gesto em andamento
  const startYRef = useRef<number>(0);
  const startXRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const activeRef = useRef<boolean>(false);
  const lockedAxisRef = useRef<'vertical' | 'horizontal' | null>(null);

  // Estado visual reativo
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Verifica se o alvo do toque (ou algum ancestral até o contentRef) tem
   * scroll vertical que ainda não chegou ao topo. Se sim, abortamos — o
   * usuário está rolando o conteúdo interno, não tentando fechar.
   */
  const isInternalScrollAtTop = useCallback((target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return true;
    let el: Element | null = target;
    const root = contentRef.current;
    while (el && el !== root) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        if ((el as HTMLElement).scrollTop > 0) return false;
      }
      el = el.parentElement;
    }
    return true;
  }, []);

  const reset = useCallback(() => {
    activeRef.current = false;
    lockedAxisRef.current = null;
    setIsDragging(false);
    setTranslateY(0);
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const t = e.touches[0];
      if (!t) return;
      // Só inicia se conteúdo interno está no topo
      if (!isInternalScrollAtTop(e.target)) {
        activeRef.current = false;
        return;
      }
      startYRef.current = t.clientY;
      startXRef.current = t.clientX;
      startTimeRef.current = Date.now();
      activeRef.current = true;
      lockedAxisRef.current = null;
    },
    [enabled, isInternalScrollAtTop],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || !activeRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - startYRef.current;
      const dx = t.clientX - startXRef.current;

      // Define o eixo do gesto após uns 8px de movimento
      if (lockedAxisRef.current === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        lockedAxisRef.current = Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
      }
      if (lockedAxisRef.current === 'horizontal') {
        activeRef.current = false;
        return;
      }
      // Só nos importa puxar para baixo
      if (dy <= 0) {
        if (translateY !== 0) setTranslateY(0);
        if (isDragging) setIsDragging(false);
        return;
      }
      // Resistência leve para parecer físico
      const next = dy * 0.9;
      setTranslateY(next);
      if (!isDragging) setIsDragging(true);
    },
    [enabled, translateY, isDragging],
  );

  const finish = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || !activeRef.current) {
        reset();
        return;
      }
      const t = e.changedTouches[0];
      const dy = t ? t.clientY - startYRef.current : translateY;
      const dt = Math.max(1, Date.now() - startTimeRef.current);
      const velocity = dy / dt;

      const shouldClose = dy > threshold || velocity > velocityThreshold;
      if (shouldClose) {
        // Animação curta de saída — translada para fora antes de fechar
        setIsDragging(false);
        setTranslateY(window.innerHeight);
        window.setTimeout(() => {
          onClose();
          reset();
        }, 180);
      } else {
        // Volta para o lugar
        setIsDragging(false);
        setTranslateY(0);
        activeRef.current = false;
        lockedAxisRef.current = null;
      }
    },
    [enabled, threshold, velocityThreshold, translateY, onClose, reset],
  );

  // Calcula opacidade do overlay (fade conforme arrasta)
  const overlayOpacity = enabled
    ? Math.max(0.3, 1 - translateY / (window.innerHeight || 800))
    : 1;

  // IMPORTANTE: NÃO emitimos `transform` inline em estado idle (translateY=0
  // e sem arrasto). Isso evita sobrescrever as classes Tailwind de
  // centralização (translate-x/y -50%) e as animações de entrada/saída do
  // Radix (slide-in-from-*, zoom-in-95). Em vez disso, expomos o
  // deslocamento via CSS custom property `--swipe-y` e cada consumidor
  // (dialog/sheet) compõe a transform final no className (ex.:
  // translate-y-[calc(-50%+var(--swipe-y,0px))]).
  const isIdle = translateY === 0 && !isDragging;
  const contentStyle: CSSProperties = enabled
    ? {
        ...(isIdle ? {} : { ['--swipe-y' as any]: `${translateY}px` }),
        transition: isDragging ? 'none' : 'transform 200ms ease-out',
        userSelect: isDragging ? 'none' : undefined,
        touchAction: 'pan-y',
      }
    : {};

  const overlayStyle: CSSProperties = enabled
    ? { opacity: overlayOpacity, transition: isDragging ? 'none' : 'opacity 200ms ease-out' }
    : {};

  return {
    contentRef,
    contentStyle,
    overlayStyle,
    isDragging,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: finish,
      onTouchCancel: finish,
    },
  };
}
